import CoreBase from '../../../services/engines/CoreBase';
import csCodeTemplateEngine from './CsCodeTemplateEngine';

const emptyRef = () => ({ name: '', version: '' });
const emptyProjectRef = () => ({ path: '' });
const emptyFileRef = () => ({ templateRk: '', enabled: true });

/**
 * CsCodeGenFormData — converts between selected config and form state, and builds normalized config.
 * see 87d0d8e0dfc440df88fa0ad2d269faba
 */
class CsCodeGenFormData extends CoreBase {
    _name = 'CsCodeGenFormData';

    /**
     * Builds the default RootFolder path from a HostedService name.
     * @param {string} hostedService
     * @returns {string}
     */
    defaultRootFolder = (hostedService) => {
        const d = csCodeTemplateEngine.getDefaults();
        return `r:\\TestCode\\Kdesk8\\${hostedService || d.hostedServicePlaceholder}\\src`;
    };

    /**
     * Resolves a files list from a selected sub-object, falling back to defaults when missing or
     * when it's still the old pre-20260703 boolean-object shape.
     * @param {*} selectedFiles - selected.plugin.files / selected.data.files / selected.common.files, any shape/version
     * @param {Array} defaultFiles - d.pluginFiles / d.dataFiles / d.commonFiles
     * @returns {Array<{templateRk:string, enabled:boolean}>}
     */
    _resolveFiles = (selectedFiles, defaultFiles) => (Array.isArray(selectedFiles) ? selectedFiles : [...(defaultFiles ?? [])]);

    /**
     * Creates a form state from a selected config.
     * @param {object|null} selected
     * @param {object|null} [defaultsOverride] - explicit CsCodeGenDefaults object to use instead of
     *   csCodeTemplateEngine.getDefaults() — used by LoadCsCodeGenDefaultsDlg, which lets the user
     *   explicitly pick which Defaults profile to apply rather than relying on the ambiguous
     *   "first CsCodeGenDefaults note in the cache" getDefaults() falls back to.
     * @returns {object}
     */
    fromSelected = (selected, defaultsOverride) => {
        const d = defaultsOverride ?? csCodeTemplateEngine.getDefaults();

        const r = {
            rk: selected?.rk ?? '',
            prefix: selected?.prefix ?? d.prefix,
            projectName: selected?.projectName ?? '',
            pluginName: selected?.pluginName ?? '',
            description: selected?.description ?? d.descriptionPlaceholder,
            remarks: selected?.remarks ?? '',
            author: selected?.author ?? d.author,
            hostedService: selected?.hostedService ?? '',
            rootFolder: selected?.rootFolder ?? '',
            rootFolderEdited: selected?.rootFolderEdited ?? false,

            sharedPackages: selected?.sharedPackages ?? [...d.sharedPackages],
            baseUsings: selected?.baseUsings ?? [...d.baseUsings],
            commonUsings: selected?.commonUsings ?? [...d.commonUsings],

            plugin: {
                enabled: selected?.plugin?.enabled ?? true,
                files: this._resolveFiles(selected?.plugin?.files, d.pluginFiles),
                packageAdditions: selected?.plugin?.packageAdditions ?? [],
                projectReferences: selected?.plugin?.projectReferences ?? [...d.pluginProjectReferences],
                overrideProjectFile: selected?.plugin?.overrideProjectFile ?? true,
            },
            data: {
                enabled: selected?.data?.enabled ?? true,
                files: this._resolveFiles(selected?.data?.files, d.dataFiles),
                packageAdditions: selected?.data?.packageAdditions ?? [...d.dataPackageAdditions],
                projectReferences: selected?.data?.projectReferences ?? [],
                overrideProjectFile: selected?.data?.overrideProjectFile ?? true,
            },
            dataSchema: {
                enabled: selected?.dataSchema?.enabled ?? true,
                files: this._resolveFiles(selected?.dataSchema?.files, d.dataSchemaFiles),
                packageAdditions: selected?.dataSchema?.packageAdditions ?? [],
                projectReferences: selected?.dataSchema?.projectReferences ?? [],
                overrideProjectFile: selected?.dataSchema?.overrideProjectFile ?? true,
            },
            common: {
                enabled: selected?.common?.enabled ?? true,
                files: this._resolveFiles(selected?.common?.files, d.commonFiles),
                packageAdditions: selected?.common?.packageAdditions ?? [],
                projectReferences: selected?.common?.projectReferences ?? [],
                overrideProjectFile: selected?.common?.overrideProjectFile ?? true,
            },
            tests: {
                enabled: selected?.tests?.enabled ?? true,
                files: this._resolveFiles(selected?.tests?.files, d.testsFiles),
                packageAdditions: selected?.tests?.packageAdditions ?? [],
                projectReferences: selected?.tests?.projectReferences ?? [],
                overrideProjectFile: selected?.tests?.overrideProjectFile ?? true,
            },
            // Default off — not every plugin publishes on its own RMQ queue.
            contracts: {
                enabled: selected?.contracts?.enabled ?? false,
                files: this._resolveFiles(selected?.contracts?.files, d.contractsFiles),
                packageAdditions: selected?.contracts?.packageAdditions ?? [...(d.contractsPackageAdditions ?? [])],
                projectReferences: selected?.contracts?.projectReferences ?? [],
                overrideProjectFile: selected?.contracts?.overrideProjectFile ?? true,
            },
        };

        return r;
    };

    /**
     * Returns a default form state for a new CsCodeGen config.
     * @returns {object}
     */
    createDefault = () => {
        const d = csCodeTemplateEngine.getDefaults();
        return this.fromSelected({
            prefix: d.prefix,
            projectName: d.projectNamePlaceholder,
            pluginName: d.pluginNamePlaceholder,
            author: d.author,
            hostedService: d.hostedServicePlaceholder,
            rootFolder: this.defaultRootFolder(d.hostedServicePlaceholder),
            rootFolderEdited: false,
            sharedPackages: [...d.sharedPackages],
            baseUsings: [...d.baseUsings],
            commonUsings: [...d.commonUsings],
        });
    };

    emptyRef = emptyRef;
    emptyProjectRef = emptyProjectRef;
    emptyFileRef = emptyFileRef;

    /**
     * Builds a normalized config object from raw form state.
     * The 3rd Plugin project reference (to the plugin's own Common project) is NOT stored here —
     * CsCodeGenGenerator auto-injects it at generation time, only when common.enabled is true.
     * @param {object} form - raw form state
     * @returns {object} normalized config
     */
    toConfig = (form) => ({
        rk: form.rk,
        prefix: form.prefix.trim() || csCodeTemplateEngine.getDefaults().prefix,
        projectName: form.projectName.trim(),
        pluginName: form.pluginName.trim() || form.projectName.trim(),
        description: form.description ?? '',
        remarks: form.remarks?.trim() ?? '',
        author: form.author?.trim() || csCodeTemplateEngine.getDefaults().author,
        hostedService: form.hostedService.trim(),
        rootFolder: form.rootFolder.trim() || this.defaultRootFolder(form.hostedService.trim()),

        sharedPackages: (form.sharedPackages ?? []).filter((p) => p.name?.trim()),
        baseUsings: (form.baseUsings ?? []).filter((u) => u?.trim()),
        commonUsings: (form.commonUsings ?? []).filter((u) => u?.trim()),

        plugin: {
            enabled: !!form.plugin?.enabled,
            files: (Array.isArray(form.plugin?.files) ? form.plugin.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.plugin?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.plugin?.projectReferences ?? []).filter((p) => p.path?.trim()),
            overrideProjectFile: form.plugin?.overrideProjectFile !== false,
        },
        data: {
            enabled: !!form.data?.enabled,
            files: (Array.isArray(form.data?.files) ? form.data.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.data?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.data?.projectReferences ?? []).filter((p) => p.path?.trim()),
            overrideProjectFile: form.data?.overrideProjectFile !== false,
        },
        dataSchema: {
            enabled: !!form.dataSchema?.enabled,
            files: (Array.isArray(form.dataSchema?.files) ? form.dataSchema.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.dataSchema?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.dataSchema?.projectReferences ?? []).filter((p) => p.path?.trim()),
            overrideProjectFile: form.dataSchema?.overrideProjectFile !== false,
        },
        common: {
            enabled: !!form.common?.enabled,
            files: (Array.isArray(form.common?.files) ? form.common.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.common?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.common?.projectReferences ?? []).filter((p) => p.path?.trim()),
            overrideProjectFile: form.common?.overrideProjectFile !== false,
        },
        tests: {
            enabled: !!form.tests?.enabled,
            files: (Array.isArray(form.tests?.files) ? form.tests.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.tests?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.tests?.projectReferences ?? []).filter((p) => p.path?.trim()),
            overrideProjectFile: form.tests?.overrideProjectFile !== false,
        },
        contracts: {
            enabled: !!form.contracts?.enabled,
            files: (Array.isArray(form.contracts?.files) ? form.contracts.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.contracts?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.contracts?.projectReferences ?? []).filter((p) => p.path?.trim()),
            overrideProjectFile: form.contracts?.overrideProjectFile !== false,
        },
    });
}

const csCodeGenFormData = new CsCodeGenFormData();
export default csCodeGenFormData;
